import React, { useState, useEffect, } from 'react';
import { useParams, useNavigate,useLocation} from 'react-router-dom';
import { ArrowLeft, User, Check, Phone, Building, LayoutDashboard, MapPin, Camera, X, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../apiConfig/api';

import PhotoLocationCapture from '../components/photolocationcapture';

// Keep DetailSection Component exactly the same
// Update DetailSection Component
const DetailSection = ({ title, children }) => (
  <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 mb-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
    <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
      <div className="h-8 w-1 bg-sky-500 rounded-full"></div> {/* Changed from bg-blue-500 to bg-sky-500 */}
      {title}
    </h3>
    <div className="animate-fadeIn">
      {children}
    </div>
  </div>
);

// Keep DetailItem Component exactly the same
const DetailItem = ({ label, value, icon: Icon }) => (
  <div className="p-5 bg-white rounded-xl border border-gray-200 hover:border-blue-500 transition-all duration-300 group">
    <div className="flex items-center gap-3 mb-2">
      {Icon && <Icon className="h-5 w-5 text-sky-500 group-hover:scale-110 transition-transform duration-300" />}
      <label className="text-sm font-semibold text-gray-600">{label}</label>
    </div>
    <p className="text-lg font-semibold text-gray-900 pl-8">{value}</p>
  </div>
);
const PropertyDetailsForm = ({ 
  onChange, 
  data, 
  propertyPhotos, 
  setPropertyPhotos, 
  previousArea, 
  previousUsage,
  entityDetails,
  setEntityDetails,
  propertyData,
  commercialArea,
  setCommercialArea,
  hasBusiness,
  setHasBusiness,
  businessLocation,
  setBusinessLocation,
  businessWardNo,
  setBusinessWardNo,
  businessAddress,
  setBusinessAddress,
  businessName,
  setBusinessName,
  businessType,
  setBusinessType,
  businessGstNumber,
  setBusinessGstNumber,
  externalLocationType,
  setExternalLocationType
}) => {

  const [ebNumberError, setEbNumberError] = useState('');
  const [showEbError, setShowEbError] = useState(false);
  const [commercialEntities, setCommercialEntities] = useState(1);
  const [tradeLicenses, setTradeLicenses] = useState([]);
  const [isLoadingLicenses, setIsLoadingLicenses] = useState(false);
  const [profTaxes, setProfTaxes] = useState([]);
const [isLoadingProfTax, setIsLoadingProfTax] = useState(false);
  // At the top with other state variables
const [slabRates, setSlabRates] = useState([]);
const [isLoadingSlabRates, setIsLoadingSlabRates] = useState(false);

const createEmptyOccupancy = () => ({
  hasProfTax: false,
  hasTradeLicense: false,
  profTaxNo: '',
  tradeLicenseNo: '',
  gstNumber: '',
  commercialArea: '',
  firmName: '',
  firmType: '',
  salariedEmployees: 0,
  ownerType: 'same',
  ownerName: '',
  mobileNumber: '',
  employeeCounts: {
    row1: 0, row2: 0, row3: 0, row4: 0, row5: 0, row6: 0
  }
});

const setOccupancyCount = (rawCount) => {
  const count = Math.min(20, Math.max(1, Number(rawCount) || 1));
  setCommercialEntities(count);

  if (count > 1 && data.buildingUsage === 'commercial') {
    onChange('buildingUsage', 'mixed');
  } else if (count === 1 && data.buildingUsage === 'mixed') {
    onChange('buildingUsage', 'commercial');
  }

  if (data.buildingUsage !== 'residential') {
    setEntityDetails((current) => Array.from(
      { length: count },
      (_, index) => current[index] || createEmptyOccupancy()
    ));
  }
};

// Add fetchSlabRates function to VerificationPage
const fetchProfTaxes = async (orgId, wardId) => {
  setIsLoadingProfTax(true);
  try {
    const token = localStorage.getItem('final');
    
    if (!token || !orgId || !wardId) {
      console.error('Missing required parameters for fetching prof tax');
      return;
    }

    const response = await api.get(`/asset/commercial/prof-tax`, {
      params: {
        orgid: orgId,
        wid: wardId
      },
      headers: {
        'Authorization': token
      }
    });

    console.log('Prof Tax Response:', response.data);
    
    if (response.data && Array.isArray(response.data.data)) {
      setProfTaxes(response.data.data);
    } else if (Array.isArray(response.data)) {
      setProfTaxes(response.data);
    } else {
      setProfTaxes([]);
    }
  } catch (error) {
    console.error('Error fetching prof taxes:', error);
    setProfTaxes([]);
  } finally {
    setIsLoadingProfTax(false);
  }
};
const fetchSlabRates = async () => {
  setIsLoadingSlabRates(true);
  try {
    const token = localStorage.getItem('final');  
    const orgId = localStorage.getItem('org_id');

    if (!token || !orgId) {
      console.error('Authentication token or organization ID not found');
      return;
    }

    const response = await api.get(`/asset/slabrate/${orgId}`, {
      headers: {
        'Authorization': token
      }
    });

    console.log('Slab Rate Response:', response.data);

    if (response.data && !response.data.error && Array.isArray(response.data.data)) {
      // Add the first slab (0-21000) with 0 tax as you mentioned is fixed
      const firstSlab = { 
        SlabRateName: "0 - 21000", 
        SlabRateUnitFrom: 0, 
        SlabRateUnitTo: 21000, 
        ProfTaxSlabRate: 0 
      };
      
      setSlabRates([firstSlab, ...response.data.data]);
    }
  } catch (error) {
    console.error('Error fetching slab rates:', error);
  } finally {
    setIsLoadingSlabRates(false);
  }
};

// Add useEffect to call fetchSlabRates
useEffect(() => {
  if (propertyData?.organization_id) {
    fetchSlabRates();
  }
}, [propertyData?.organization_id]);

const firmTypeOptions = [
  { value: "industrial", label: "Industrial" },
  { value: "educational", label: "Educational" },
  { value: "government", label: "Government" },
  { value: "proprietorships ", label: "Proprietorships " },
  { value: "pvt Ltd", label: "Pvt Ltd" },
  { value: "public Ltd", label: "Public Ltd" },
  { value: "residential", label: "Residential" },
  { value: "others", label: "Others" },

 
];

  const externalLocationTypes = [
    { value: "villagePanchayat", label: "Village Panchayat" },
    { value: "townPanchayat", label: "Town Panchayat" },
    { value: "municipality", label: "Municipality" },
    { value: "corporation", label: "Corporation" },
    { value: "notKnown", label: "Not Known" }
  ];

useEffect(() => {
  const orgId = propertyData?.organization_id;
  const wardId = propertyData?.ward_id;
  
  if (orgId && wardId) {
    fetchTradeLicenses(orgId, wardId);
    fetchProfTaxes(orgId, wardId);
  }
}, [propertyData?.organization_id, propertyData?.ward_id]);
  useEffect(() => {
    // Function to close all dropdowns when clicking outside
    const handleClickOutside = (event) => {
      const dropdowns = document.querySelectorAll('.dropdown-container');
      let clickedOutside = true;
      
      // Check if click was inside any dropdown container
      dropdowns.forEach(dropdown => {
        if (dropdown.contains(event.target)) {
          clickedOutside = false;
        }
      });
      
      // If clicked outside, close all dropdowns
      if (clickedOutside) {
        const newDetails = entityDetails.map(entity => ({
          ...entity,
          showDropdown: false
        }));
        setEntityDetails(newDetails);
      }
    };
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [entityDetails, setEntityDetails]);

  const fetchTradeLicenses = async (orgId, wardId) => {
    setIsLoadingLicenses(true);
    try {
      const token = localStorage.getItem('final');
      
      if (!token || !orgId || !wardId) {
        console.error('Missing required parameters for fetching trade licenses');
        return;
      }
  
      const response = await api.get(`/asset/commercial/trade-license`, {
        params: {
          orgid: orgId,
          wid: wardId
        },
        headers: {
          'Authorization': token
        }
      });
  
      console.log('Trade License Response:', response.data);
      
      if (response.data && Array.isArray(response.data.data)) {
        setTradeLicenses(response.data.data);
      } else if (Array.isArray(response.data)) {
        setTradeLicenses(response.data);
      } else {
        setTradeLicenses([]);
      }
    } catch (error) {
      console.error('Error fetching trade licenses:', error);
      setTradeLicenses([]);
    } finally {
      setIsLoadingLicenses(false);
    }
  };
  const getTotalCommercialArea = () => {
    return entityDetails.reduce((total, entity) => {
      // Include all entities, regardless of type (commercial or residential)
      return total + parseFloat(entity.commercialArea || 0);
    }, 0);
  };
  const getTotalEmployeeCount = (employeeCounts) => {
    return Object.values(employeeCounts || {}).reduce((sum, count) => sum + (parseInt(count) || 0), 0);
  };
  const validateEbNumber = (value) => {
    if (!value) {
      setEbNumberError('Please enter EB number');
      return false;
    }
    if (value.length !== 12) {
      setEbNumberError('EB number must be 12 digits');
      return false;
    }
    setEbNumberError('');
    return true;
  };

  const handleEmployeeCountChange = (entityIndex, rowKey, value) => {
    const newDetails = [...entityDetails];
    const numValue = value === '' ? 0 : parseInt(value.replace(/^0+/, '')) || 0;
    if (!newDetails[entityIndex].employeeCounts) {
      newDetails[entityIndex].employeeCounts = {};
    }
    newDetails[entityIndex].employeeCounts[rowKey] = numValue;
    setEntityDetails(newDetails);
  };

  const getLicenseNumber = (entity, taxType) => {
    if (taxType === 'profTax') {
      return entity.prof_tax_no || entity.ProfTaxAssesmentNo || entity.taxNo || '';
    } else {
      // For trade license, check for the correct property names based on your API response
      return entity.TradeLicenseNo || entity.trade_license_no || entity.licenseNo || '';
    }
  };
  const calculateTax = (employeeCount, taxRate) => {
    if (!employeeCount || !taxRate) return 0;
    return employeeCount * taxRate;
  };

  return (
    <div className="mb-6 mt-10">
      <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
        <Building className="h-6 w-6 text-sky-500" />
        <span className="relative">Property Details</span>
      </h4>
      <section className="mt-10 rounded-xl border border-gray-200 bg-gray-50 p-4">
        <h5 className="mb-4 text-base font-semibold text-gray-800">As Per Record</h5>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">Usage</label>
            <input type="text" className="w-full rounded-lg border border-gray-200 bg-white p-2.5 text-gray-700" value={previousUsage || '--'} disabled />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">Builtup Area</label>
            <input type="text" className="w-full rounded-lg border border-gray-200 bg-white p-2.5 text-gray-700" value={previousArea || '--'} disabled />
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-xl border border-gray-200 p-4">
        <h5 className="mb-4 text-base font-semibold text-gray-800">Observed</h5>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="floorArea" className="mb-1.5 block text-sm font-semibold text-gray-700">Builtup Area</label>
            <input id="floorArea" type="number" min="1" className="w-full rounded-lg border border-gray-200 p-2.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500" value={data.floorArea || ''} onChange={(e) => onChange('floorArea', e.target.value)} required />
          </div>
          <div>
            <label htmlFor="occupancyCount" className="mb-1.5 block text-sm font-semibold text-gray-700">Number of Occupancy</label>
            <div className="inline-flex h-[46px] items-center overflow-hidden rounded-lg border border-gray-200 bg-white">
              <button type="button" className="h-full px-4 text-xl text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300" onClick={() => setOccupancyCount(commercialEntities - 1)} disabled={commercialEntities <= 1} aria-label="Decrease occupancy count">−</button>
              <output id="occupancyCount" className="flex h-full min-w-14 items-center justify-center border-x border-gray-200 px-4 font-semibold text-gray-800" aria-live="polite">{commercialEntities}</output>
              <button type="button" className="h-full px-4 text-xl text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300" onClick={() => setOccupancyCount(commercialEntities + 1)} disabled={commercialEntities >= 20} aria-label="Increase occupancy count">+</button>
            </div>
          </div>
          <div>
            <label htmlFor="buildingUsage" className="mb-1.5 block text-sm font-semibold text-gray-700">Usage</label>
            <select
              id="buildingUsage"
              className="w-full rounded-lg border border-gray-200 p-2.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              value={data.buildingUsage || ''}
              onChange={(e) => {
                const value = e.target.value;
                onChange('buildingUsage', value);
                setCommercialArea('');
                setEntityDetails(value === 'residential' ? [] : Array.from({ length: commercialEntities }, () => createEmptyOccupancy()));
                if (value !== 'residential') {
                  setHasBusiness('no');
                  setBusinessLocation('withinULB');
                  setBusinessWardNo('');
                  setBusinessAddress('');
                  setBusinessName('');
                  setBusinessType('');
                  setExternalLocationType('');
                }
              }}
              required
            >
              <option value="">Select Usage</option>
              <option value="residential">Residential</option>
              {commercialEntities === 1 ? (
                <option value="commercial">Non Residential</option>
              ) : (
                <option value="mixed">Mixed</option>
              )}
            </select>
          </div>
        </div>
      </section>
  
      {data.buildingUsage === 'residential' && (
        <div className="mt-8 border-t border-gray-200 pt-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Does the applicant have business elsewhere?
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasBusiness"
                    value="yes"
                    checked={hasBusiness === 'yes'}
                    onChange={(e) => setHasBusiness(e.target.value)}
                    className="mr-2"
                  />
                  Yes
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="hasBusiness"
                    value="no"
                    checked={hasBusiness === 'no'}
                    onChange={(e) => setHasBusiness(e.target.value)}
                    className="mr-2"
                  />
                  No
                </label>
              </div>
            </div>
  
            {hasBusiness === 'yes' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Is the business within ULB?
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="businessLocation"
                        value="withinULB"
                        checked={businessLocation === 'withinULB'}
                        onChange={(e) => setBusinessLocation(e.target.value)}
                        className="mr-2"
                      />
                      Yes
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="businessLocation"
                        value="outsideULB"
                        checked={businessLocation === 'outsideULB'}
                        onChange={(e) => setBusinessLocation(e.target.value)}
                        className="mr-2"
                      />
                      No
                    </label>
                  </div>
                </div>
  
                {businessLocation === 'withinULB' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Ward No
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200 rounded-xl"
                        value={businessWardNo}
                        onChange={(e) => setBusinessWardNo(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Business Name
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200 rounded-xl"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Business Type
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200 rounded-xl"
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Address
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200 rounded-xl"
                        value={businessAddress}
                        onChange={(e) => setBusinessAddress(e.target.value)}
                      />
                    </div>
                    <div>
  <label className="block text-sm font-semibold text-gray-700 mb-2">
    GST Number
  </label>
  <input
    type="text"
    className="w-full p-4 border border-gray-200 rounded-xl"
    value={businessGstNumber}
    onChange={(e) => setBusinessGstNumber(e.target.value)}
    placeholder="Enter GST Number"
  />
</div>
                  </div>
                ) : businessLocation === 'outsideULB' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Location Type
                      </label>
                      <select
                        className="w-full p-4 border border-gray-200 rounded-xl"
                        value={externalLocationType}
                        onChange={(e) => setExternalLocationType(e.target.value)}
                      >
                        <option value="">Select Location Type</option>
                        {externalLocationTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Business Address
                      </label>
                      <input
                        type="text"
                        className="w-full p-4 border border-gray-200 rounded-xl"
                        value={businessAddress}
                        onChange={(e) => setBusinessAddress(e.target.value)}
                      />
                    </div>
                    <div>
  <label className="block text-sm font-semibold text-gray-700 mb-2">
    GST Number
  </label>
  <input
    type="text"
    className="w-full p-4 border border-gray-200 rounded-xl"
    value={businessGstNumber}
    onChange={(e) => setBusinessGstNumber(e.target.value)}
    placeholder="Enter GST Number"
  />
</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
  
  {data.buildingUsage !== 'residential' && (
        <div className="mt-8 border-t border-gray-200">
          <div className="pt-6 space-y-6">
{entityDetails.length > 0 && entityDetails.map((entity, index) => (
  <div key={index} className="border p-4 rounded-lg space-y-4 relative">
<div className="flex justify-between items-center">
  <div className="flex items-center gap-4">
    <h5 className="font-semibold">Occupancy {index + 1}/{entityDetails.length}</h5>
  </div>
</div>
    
{/* First row: Type, owner and area */}
<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      Area Used
    </label>
    <div className="relative">
      <input
        data-field="commercialArea"
        type="number"
        min="100"
        className={`w-full p-2 border ${parseFloat(entity.commercialArea || 0) > 0 && getTotalCommercialArea() > parseFloat(data.floorArea || 0) ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'} rounded-lg`}
        value={entity.commercialArea || ''}
        onChange={(e) => {
          const newDetails = [...entityDetails];
          newDetails[index] = { ...newDetails[index], commercialArea: e.target.value };
          setEntityDetails(newDetails);
        }}
        placeholder="Minimum 100 sq.ft"
        required
      />
    </div>
  </div>

  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2">Owner</label>
    <select
      className="w-full p-2 border border-gray-200 rounded-lg"
      value={entity.ownerType || 'same'}
      onChange={(e) => {
        const newDetails = [...entityDetails];
        newDetails[index] = {
          ...newDetails[index],
          ownerType: e.target.value,
          ownerName: e.target.value === 'same' ? '' : newDetails[index].ownerName,
          mobileNumber: e.target.value === 'same' ? '' : newDetails[index].mobileNumber
        };
        setEntityDetails(newDetails);
      }}
    >
      <option value="same">Same</option>
      <option value="other">Other</option>
    </select>
    {entity.ownerType === 'other' && (
      <div className="mt-2 space-y-2">
        <input
          type="text"
          className="w-full p-2 border border-gray-200 rounded-lg"
          value={entity.ownerName || ''}
          onChange={(e) => {
            const newDetails = [...entityDetails];
            newDetails[index] = { ...newDetails[index], ownerName: e.target.value };
            setEntityDetails(newDetails);
          }}
          placeholder="Owner name"
          aria-label={`Occupancy ${index + 1} owner name`}
          required
        />
        <input
          type="tel"
          inputMode="numeric"
          maxLength="10"
          className="w-full p-2 border border-gray-200 rounded-lg"
          value={entity.mobileNumber || ''}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
            const newDetails = [...entityDetails];
            newDetails[index] = { ...newDetails[index], mobileNumber: value };
            setEntityDetails(newDetails);
          }}
          placeholder="10-digit mobile number"
          aria-label={`Occupancy ${index + 1} owner mobile number`}
          required
        />
      </div>
    )}
  </div>

  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
    <select
      data-field="firmType"
      className="w-full p-2 border border-gray-200 rounded-lg"
      value={entity.firmType || ''}
      onChange={(e) => {
        const newDetails = [...entityDetails];
        newDetails[index] = { ...newDetails[index], firmType: e.target.value };
        setEntityDetails(newDetails);
      }}
      required
    >
      <option value="">Select Type</option>
      {firmTypeOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
  </div>
</div>

{entity.firmType && entity.firmType !== 'residential' && (
  <div className="mb-4 flex items-center gap-2">
    <label className="text-sm font-medium text-gray-600">Number of Salaried Employees:</label>
    <input
      data-field="salariedEmployees"
      type="number"
      min="0"
      className="w-20 rounded border border-gray-200 p-1 text-right"
      value={entity.salariedEmployees || 0}
      onChange={(e) => {
        const newDetails = [...entityDetails];
        newDetails[index] = { ...newDetails[index], salariedEmployees: e.target.value === '' ? 0 : parseInt(e.target.value) };
        setEntityDetails(newDetails);
      }}
    />
  </div>
)}
    
{/* Second row: Firm Type and GST Number side by side */}
{/* Second row: Registration Types and GST Number */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
  {entity.firmType && entity.firmType !== 'residential' && (
    <>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Registration Types
        </label>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name={`profTax-${index}`}
              checked={entity.hasProfTax}
              onChange={() => {
                const newDetails = [...entityDetails];
                newDetails[index] = {
                  ...newDetails[index],
                  hasProfTax: !entity.hasProfTax,
                  profTaxNo: entity.hasProfTax ? '' : entity.profTaxNo
                };
                setEntityDetails(newDetails);
              }}
              className="mr-2"
            />
            Professional Tax
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              name={`tradeLicense-${index}`}
              checked={entity.hasTradeLicense}
              onChange={() => {
                const newDetails = [...entityDetails];
                newDetails[index] = {
                  ...newDetails[index],
                  hasTradeLicense: !entity.hasTradeLicense,
                  tradeLicenseNo: entity.hasTradeLicense ? '' : entity.tradeLicenseNo
                };
                setEntityDetails(newDetails);
              }}
              className="mr-2"
            />
            Trade License
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          GST Number
        </label>
        <input
          type="text"
          className="w-full p-2 border border-gray-200 rounded-lg"
          value={entity.gstNumber || ''}
          onChange={(e) => {
            const value = e.target.value;
            const newDetails = [...entityDetails];
            newDetails[index] = {
              ...newDetails[index],
              gstNumber: value
            };
            setEntityDetails(newDetails);
          }}
          placeholder="Enter GST Number"
        />
      </div>
    </>
  )}
</div>
   {/* Tax Number Fields */}
{entity.firmType && entity.firmType !== 'residential' && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
{entity.hasProfTax && (
  <div className="relative">
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      Professional Tax Number
    </label>
    <select
      className="w-full p-2 border border-gray-200 rounded-lg"
      value={entity.profTaxNo || ''}
      onChange={(e) => {
        const value = e.target.value;
        
        // Check if this tax number is already selected in another entity
        const isDuplicate = entityDetails.some((otherEntity, otherIdx) => 
          otherIdx !== index && 
          otherEntity.hasProfTax && 
          otherEntity.profTaxNo === value
        );
        
        if (isDuplicate && value) {
          alert(`Tax number ${value} is already selected for another entity.`);
          return;
        }
        
        const newDetails = [...entityDetails];
        newDetails[index] = {
          ...newDetails[index],
          profTaxNo: value
        };
        setEntityDetails(newDetails);
        
        // Update the main propertyDetails state
        if (value) {
          onChange('hasProfessionalTax', true);
          onChange('professionalTaxId', value);
        }
      }}
      disabled={isLoadingProfTax}
    >
      <option value="">Select Professional Tax Number</option>
      {profTaxes
        .filter(tax => {
          // Only show tax numbers that are not used (used === "no")
          if (tax.used === "yes") {
            return false;
          }
          
          // Also filter out entries with empty ProfTaxAssesmentNo
          if (!tax.ProfTaxAssesmentNo || tax.ProfTaxAssesmentNo.trim() === '') {
            return false;
          }
          
          return true;
        })
        .map((tax, idx) => {
          const taxNo = tax.prof_tax_no || tax.ProfTaxAssesmentNo || tax.taxNo;
          return (
            <option key={idx} value={taxNo}>
              {taxNo}
            </option>
          );
        })}
    </select>
  </div>
)}
{entity.hasTradeLicense && (
  <div className="relative">
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      Trade License Number
    </label>
    <select
      className="w-full p-2 border border-gray-200 rounded-lg"
      value={entity.tradeLicenseNo || ''}
      onChange={(e) => {
        const value = e.target.value;
        
        // Check if this license number is already selected in another entity
        const isDuplicate = entityDetails.some((otherEntity, otherIdx) => 
          otherIdx !== index && 
          otherEntity.hasTradeLicense && 
          otherEntity.tradeLicenseNo === value
        );
        
        if (isDuplicate && value) {
          alert(`License number ${value} is already selected for another entity.`);
          return;
        }
        
        const newDetails = [...entityDetails];
        newDetails[index] = {
          ...newDetails[index],
          tradeLicenseNo: value
        };
        setEntityDetails(newDetails);
        
        // Update the main propertyDetails state
        if (value) {
          onChange('hasTradeLicense', true);
          onChange('tradeLicenseId', value);
        }
      }}
      disabled={isLoadingLicenses}
    >
      <option value="">Select Trade License Number</option>
      {tradeLicenses
        .filter(license => {
          // Only show licenses that are not used (used === "no")
          if (license.used === "yes") {
            return false;
          }
          
          const licenseNo = (license.TradeLicenseNo || license.trade_license_no || license.licenseNo || '').toString();
          return licenseNo.trim() !== '';
        })
        .map((license, idx) => {
          const licenseNo = license.TradeLicenseNo || license.trade_license_no || license.licenseNo;
          return (
            <option key={idx} value={licenseNo}>
              {licenseNo}
            </option>
          );
        })}
    </select>
  </div>
)}
    </div>
)}

    {entity.firmType && entity.firmType !== 'residential' && isLoadingProfTax && entity.hasProfTax && (
      <div className="flex items-center gap-2 text-blue-600">
        <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
        <span className="text-sm">Loading Professional Tax Numbers...</span>
      </div>
    )}

    {entity.firmType && entity.firmType !== 'residential' && isLoadingLicenses && entity.hasTradeLicense && (
      <div className="flex items-center gap-2 text-blue-600">
        <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
        <span className="text-sm">Loading Trade License Numbers...</span>
      </div>
    )}

    {entity.firmType && entity.firmType !== 'residential' && !entity.hasProfTax && !entity.hasTradeLicense && (
      <div className="grid grid-cols-1 gap-4 mb-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Name of Firm
          </label>
          <input
            type="text"
            className="w-full p-3 border border-gray-200 rounded-lg"
            value={entity.firmName || ''}
            onChange={(e) => {
              const newDetails = [...entityDetails];
              newDetails[index] = {
                ...newDetails[index],
                firmName: e.target.value
              };
              setEntityDetails(newDetails);
            }}
            required
          />
        </div>
      </div>
    )}

    {/* Employee Count Table */}
    {entity.firmType && entity.firmType !== 'residential' && (
    <div className="w-full lg:w-full mt-4">
      {isLoadingSlabRates ? (
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
          <span className="ml-2">Loading tax rates...</span>
        </div>
      ) : (
        <>
          <div className="hidden lg:block">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      Half-Yearly Income Range
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">
                      Tax Rate (₹)
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                      Number of Employees
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                      Tax Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {slabRates.map((slab, slabIndex) => (
                    <tr key={slabIndex} className={slabIndex % 2 === 0 ? '' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-xs text-gray-900">
                        ₹{slab.SlabRateUnitFrom.toLocaleString()} - ₹{slab.SlabRateUnitTo.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-900">
                        ₹{slab.ProfTaxSlabRate}
                      </td>
                      <td className="px-3 py-2 text-right">
                      <div className="relative">
  <input
    type="number"
    min="0"
    className={`w-20 p-1 border rounded text-right ${
      getTotalEmployeeCount(entity.employeeCounts) > (entity.salariedEmployees || 0) 
        ? 'border-red-500 focus:ring-red-500' 
        : 'border-gray-200 focus:ring-blue-500'
    }`}
    value={entity.employeeCounts?.[`row${slabIndex + 1}`] || 0}
    onChange={(e) => handleEmployeeCountChange(index, `row${slabIndex + 1}`, e.target.value)}
  />
</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-right">
                        {slab.ProfTaxSlabRate === 0 ? 'Nil' : 
                          `₹${calculateTax(entity.employeeCounts?.[`row${slabIndex + 1}`], slab.ProfTaxSlabRate)}`}
                      </td>
                    </tr>
                  ))}
           <tr className="bg-gray-100 font-medium">
  <td className="px-3 py-2 text-xs text-gray-900">Total</td>
  <td className="px-3 py-2 text-xs text-center">-</td>
  <td className="px-3 py-2 text-xs text-right">
    <div className="flex flex-col items-end">
      <span className={getTotalEmployeeCount(entity.employeeCounts) > (entity.salariedEmployees || 0) ? 'text-red-500' : ''}>
        {getTotalEmployeeCount(entity.employeeCounts)}
      </span>
      {getTotalEmployeeCount(entity.employeeCounts) > (entity.salariedEmployees || 0) && (
        <span className="text-red-500 text-xs font-normal">
          Exceeds salaried count
        </span>
      )}
    </div>
  </td>
  <td className="px-3 py-2 text-xs text-right">
    ₹{slabRates.reduce((total, slab, slabIndex) => {
      return total + calculateTax(entity.employeeCounts?.[`row${slabIndex + 1}`], slab.ProfTaxSlabRate);
    }, 0)}
  </td>
</tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="block lg:hidden space-y-2 p-2">
            {slabRates.map((slab, slabIndex) => (
              <div key={slabIndex} className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                <div className="text-xs font-medium text-gray-900 mb-3">
                  ₹{slab.SlabRateUnitFrom.toLocaleString()} - ₹{slab.SlabRateUnitTo.toLocaleString()}
                </div>
                <div className="flex flex-col space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-xs text-gray-500">Tax Rate</label>
                    <span className="text-xs font-medium text-gray-900">₹{slab.ProfTaxSlabRate}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <label className="text-xs text-gray-500">Employees</label>
                    <div className="relative">
  <input
    type="number"
    min="0"
    className={`w-20 p-1 border rounded text-right ${
      getTotalEmployeeCount(entity.employeeCounts) > (entity.salariedEmployees || 0) 
        ? 'border-red-500 focus:ring-red-500' 
        : 'border-gray-200 focus:ring-blue-500'
    }`}
    value={entity.employeeCounts?.[`row${slabIndex + 1}`] || 0}
    onChange={(e) => handleEmployeeCountChange(index, `row${slabIndex + 1}`, e.target.value)}
  />
</div>
                  </div>
                  <div className="flex justify-between items-center">
                    <label className="text-xs text-gray-500">Amount</label>
                    <span className="text-xs font-medium text-gray-900">
                      {slab.ProfTaxSlabRate === 0 ? 'Nil' : 
                        `₹${calculateTax(entity.employeeCounts?.[`row${slabIndex + 1}`], slab.ProfTaxSlabRate)}`}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* Summary Card */}
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="flex justify-between items-center mb-2">
  <span className="text-xs font-medium text-gray-700">Total Employees</span>
  <span className={`text-xs font-semibold ${
    getTotalEmployeeCount(entity.employeeCounts) > (entity.salariedEmployees || 0) 
      ? 'text-red-500' 
      : 'text-blue-600'
  }`}>
    {getTotalEmployeeCount(entity.employeeCounts)}
    {getTotalEmployeeCount(entity.employeeCounts) > (entity.salariedEmployees || 0) && (
      <span className="text-red-500 text-xs ml-1">(Exceeds salaried count)</span>
    )}
  </span>
</div>
            </div>
          </div>
        </>
      )}
    </div>
    )}
  </div>
))}
{entityDetails.length > 0 && (
  <div className="flex justify-center mt-4">

  </div>
)}

{/* Area validation summary */}
{entityDetails.length > 0 && (
  <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
    <div className="flex items-center justify-between">
      <h6 className="text-lg font-semibold text-gray-700">Total Area Summary</h6>
      <div className="text-lg font-bold">{getTotalCommercialArea()} / {data.floorArea} sq.ft</div>
    </div>
    
    {getTotalCommercialArea() > parseFloat(data.floorArea || 0) && (
      <p className="mt-2 text-sm text-red-600 font-medium">
        Total area of all entities ({getTotalCommercialArea()} sq.ft) cannot exceed build area ({data.floorArea} sq.ft)
      </p>
    )}
    
    {getTotalCommercialArea() < parseFloat(data.floorArea || 0) && (
      <p className="mt-2 text-sm text-orange-600 font-medium">
        Total area of all entities ({getTotalCommercialArea()} sq.ft) must equal build area ({data.floorArea} sq.ft)
      </p>
    )}
    
    {getTotalCommercialArea() === parseFloat(data.floorArea || 0) && (
      <p className="mt-2 text-sm text-green-600 font-medium">
        Perfect! Total area matches build area.
      </p>
    )}
  </div>
)}
          </div>
        </div>
      )}
      <div className="mt-8">
        <label className="block text-sm font-semibold text-gray-700 mb-4">
          Property Photos with Location (Max 3)
        </label>

<PhotoLocationCapture 
  showSelfie={false}
  onPhotoCaptured={(photos) => {
    console.log('Photos captured:', photos);
    setPropertyPhotos(photos);
  }}
/>
      </div>
    </div>
  );
}




const VerificationPage = () => {
  const { id, asst_ref } = useParams();
  const navigate = useNavigate();
  const [propertyData, setPropertyData] = useState(null);
  const [ownerVerified, setOwnerVerified] = useState(true);
  const [slabRates, setSlabRates] = useState([]);
const [isLoadingSlabRates, setIsLoadingSlabRates] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [totalArea, setTotalArea] = useState('');
  const [buildingStructure, setBuildingStructure] = useState('ac');
  const [buildingType, setBuildingType] = useState('independent');
  const [entityDetails, setEntityDetails] = useState([]);
  const [apartmentFloor, setApartmentFloor] = useState('');
  const [commercialArea, setCommercialArea] = useState('');
  const location = useLocation();
const [mobileVerified, setMobileVerified] = useState(true);

  const isFromAssets = sessionStorage.getItem('fromAssets') === 'true';
  const [propertyDetails, setPropertyDetails] = useState({
    floorArea: '',
    buildingUsage: '',
    ebNumber: '',
    hasProfessionalTax: true,
    professionalTaxId: ''
  });

  // Add new state variables for residential business
  const [hasBusiness, setHasBusiness] = useState('no');
  const [businessLocation, setBusinessLocation] = useState('withinULB');
  const [businessWardNo, setBusinessWardNo] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [externalLocationType, setExternalLocationType] = useState('');
  const [businessGstNumber, setBusinessGstNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [isMobileValid, setIsMobileValid] = useState(true);
  const [showMobileError, setShowMobileError] = useState(false);
  const [totalFloors, setTotalFloors] = useState('');
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newOwnerName, setNewOwnerName] = useState('');
  const [isBuilding, setIsBuilding] = useState(true);
  const [currentUsage, setCurrentUsage] = useState('');
  const [propertyPhotos, setPropertyPhotos] = useState([]);

  
  const [formData, setFormData] = useState({
    zoneId: '',
    wardId: '',
    areaId: '',
    localityId: '',
  });
  // Add fetchSlabRates function to VerificationPage

const fetchSlabRates = async () => {
  setIsLoadingSlabRates(true);
  try {
    const token = localStorage.getItem('final');  
    const orgId = localStorage.getItem('org_id');

    if (!token || !orgId) {
      console.error('Authentication token or organization ID not found');
      return;
    }

    const response = await api.get(`/asset/slabrate/${orgId}`, {
      headers: {
        'Authorization': token
      }
    });

    console.log('Slab Rate Response:', response.data);

    if (response.data && !response.data.error && Array.isArray(response.data.data)) {
      // Add the first slab (0-21000) with 0 tax as you mentioned is fixed
      const firstSlab = { 
        SlabRateName: "0 - 21000", 
        SlabRateUnitFrom: 0, 
        SlabRateUnitTo: 21000, 
        ProfTaxSlabRate: 0 
      };
      
      setSlabRates([firstSlab, ...response.data.data]);
    }
  } catch (error) {
    console.error('Error fetching slab rates:', error);
  } finally {
    setIsLoadingSlabRates(false);
  }
};


// Add useEffect to call fetchSlabRates
useEffect(() => {
  if (propertyData?.organization_id) {
    fetchSlabRates();
  }
}, [propertyData?.organization_id]);
  const validateMobileNumber = (number) => {
    const isValid = /^[6-9]\d{9}$/.test(number);
    setIsMobileValid(isValid);
    return isValid;
  };
  const [isLoadingLicenses, setIsLoadingLicenses] = useState(false);
  const [tradeLicenses, setTradeLicenses] = useState([]);
  const fetchTradeLicenses = async () => {
    setIsLoadingLicenses(true);
    try {
      const token = localStorage.getItem('final');  
      const orgId = localStorage.getItem('org_id');
  
      if (!token) {
        console.error('Authentication token not found');
        return;
      }
  
      const wardId = propertyData?.ward_id;
  
      if (!orgId || !wardId) {
        console.error('Missing organization code or ward ID');
        return;
      }
  
      const response = await api.get(`/user/proftax?orgid=${orgId}&wid=${wardId}`, {
        headers: {
          'Authorization': token
        }
      });
  
      console.log('Trade License Response:', response.data); // Debug log
  
      const data = response.data;
      if (Array.isArray(data)) {
        console.log('Trade Licenses:', data); // Debug log
        setTradeLicenses(data);
      } else if (data.data && Array.isArray(data.data)) {
        // If the data is nested under a 'data' property
        console.log('Trade Licenses:', data.data); // Debug log
        setTradeLicenses(data.data);
      } else {
        console.log('No valid trade license data found:', data); // Debug log
        setTradeLicenses([]);
      }
  
    } catch (error) {
      console.error('Error fetching trade licenses:', error);
      setTradeLicenses([]);
    } finally {
      setIsLoadingLicenses(false);
    }
  };
  const calculateTax = (employeeCount, taxRate) => {
    if (!employeeCount || !taxRate) return 0;
    return employeeCount * taxRate;
  };
  

// Add this at the top of your component, right after your state declarations:
useEffect(() => {
  const fetchPropertyDetails = async () => {
    try {
      setLoading(true);
      
      // Get the assessment number from URL params
      const decodedId = decodeURIComponent(id);
      
      // Call the API with the assessment number
      const response = await api.get(`/asset/byref`, {
        params: { asst_ref: decodedId }
      });
      
      if (response.data && !response.data.error) {
        const property = response.data.data;
        
// In your fetchPropertyDetails function, modify the setPropertyData call:
setPropertyData({
  id: property.id, // Add this line to include the id field
  area_name: property.org_name || '',
  zone_name: property.zone_name || '',
  ward_name: property.ward_name || '',
  street_name: property.street_name || '',
  asst_ref: property.asst_ref || decodedId,
  owner: property.owner || '',
  build_area: property.area || '',
  zone_id: property.zone_id || '',
  ward_id: property.ward_id || '',
  area_id: property.locality_id || '',
  loc_id: property.locality_id || '',
  organization_id: property.org_id || '',
  usage_type: property.usage_type || property.usage || 'Residential',
  eb_number: property.eb_no || '',
  gst_number: property.gst_no || '',
  mobile_number: property.mobile || ''
});
        // Store organization ID in localStorage for other API calls if needed
        localStorage.setItem('org_id', property.org_id);
        
        // Initialize property details WITHOUT setting floorArea
        setPropertyDetails(prev => ({
          ...prev,
          floorArea: '', // Keep this empty for user input
          buildingUsage: 'residential', // Default to residential
          ebNumber: property.eb_no || '' // Pre-fill EB number
        }));
        
        // Set mobile number if available
        if (property.mobile) {
          setMobileNumber(property.mobile);
          validateMobileNumber(property.mobile);
        }
        
      } else {
        throw new Error(response.data?.message || 'No property data found');
      }
    } catch (error) {
      console.error('Error fetching property details:', error);
      alert(error.message || 'Error loading property details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };
  if (id) {
    fetchPropertyDetails();
  } else {
    setLoading(false);
    console.error('Missing assessment ID in URL');
    alert('Missing required parameters');
    navigate(-1);
  }
}, [id, navigate]);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 3) {
      alert('You can only upload up to 3 photos');
      return;
    }
    setPropertyPhotos(files);
  };

  const handleOwnerUpdate = () => {
    if (newOwnerName.trim()) {
      // Here you can add the API call to update the owner name
      console.log('Updating owner name to:', newOwnerName);
      // After successful update:
      setOwnerVerified(true);
    }
  };

  const removePhoto = (index) => {
    const updatedPhotos = [...propertyPhotos];
    updatedPhotos.splice(index, 1);
    setPropertyPhotos(updatedPhotos);
  };

 // Update the owner verification section in VerificationPage component

// Update the validateForm function
const validateForm = () => {
  const newErrors = {};
  let errorMessage = '';
  let firstErrorField = null;  // Store reference to the first error field for focus

  // Owner verification check
  if (!ownerVerified && !newOwnerName.trim()) {
    errorMessage += '- Please enter the new owner name\n';
    newErrors.ownerName = true;
    if (!firstErrorField) firstErrorField = 'ownerName';
  }

  if (!mobileVerified && (!mobileNumber || !validateMobileNumber(mobileNumber))) {
    errorMessage += '- Please enter a valid 10-digit replacement mobile number\n';
    newErrors.mobileNumber = true;
    if (!firstErrorField) firstErrorField = 'mobileNumber';
  }

  if (isBuilding) {
    // Common Building Validations
    if (!propertyDetails.floorArea) {
      errorMessage += '- Build Area as Observed is required\n';
      newErrors.floorArea = true;
      if (!firstErrorField) firstErrorField = 'floorArea';
    }

    if (!propertyDetails.buildingUsage) {
      errorMessage += '- Building usage is required\n';
      newErrors.buildingUsage = true;
      if (!firstErrorField) firstErrorField = 'buildingUsage';
    }

    // Validate entity areas for all non-residential usage types
    if (propertyDetails.buildingUsage === 'commercial' || propertyDetails.buildingUsage === 'mixed' || 
        propertyDetails.buildingUsage === 'educational' || propertyDetails.buildingUsage === 'government' || 
        propertyDetails.buildingUsage === 'industrial') {
      
      const entitiesWithoutType = entityDetails.filter(entity => !entity.firmType);
      if (entitiesWithoutType.length > 0) {
        errorMessage += '- Type is required for every occupancy\n';
        newErrors.firmType = true;
        if (!firstErrorField) firstErrorField = 'firmType';
      }

      const invalidOtherOwners = entityDetails.filter(entity =>
        entity.ownerType === 'other' &&
        (!entity.ownerName?.trim() || !/^\d{10}$/.test(entity.mobileNumber || ''))
      );
      if (invalidOtherOwners.length > 0) {
        errorMessage += '- Other owner requires a name and valid 10-digit mobile number\n';
        newErrors.occupancyOwner = true;
        if (!firstErrorField) firstErrorField = 'occupancyOwner';
      }

      // Every occupancy must use at least 100 sq.ft.
      const entitiesWithoutArea = entityDetails.filter(entity => 
        !entity.commercialArea || parseFloat(entity.commercialArea) < 100
      );
      
      if (entitiesWithoutArea.length > 0) {
        errorMessage += '- Area Used is required for every occupancy and must be at least 100 sq.ft\n';
        newErrors.commercialArea = true;
        if (!firstErrorField) firstErrorField = 'commercialArea';
      }
      
      // Calculate total area across all entities
      const totalEntityArea = entityDetails.reduce((sum, entity) => {
        return sum + (parseFloat(entity.commercialArea || 0));
      }, 0);
      
      // Check if total area matches build area with a small tolerance for floating point errors
      if (Math.abs(totalEntityArea - parseFloat(propertyDetails.floorArea || 0)) > 0.01) {
        errorMessage += `- Total area of all entities (${totalEntityArea} sq.ft) must equal build area (${propertyDetails.floorArea} sq.ft)\n`;
        newErrors.commercialArea = true;
        if (!firstErrorField) firstErrorField = 'commercialArea';
      }

      // Check for duplicate tax numbers
      if (entityDetails.length > 1) {
        // Check for duplicate prof tax numbers
        const profTaxNumbers = entityDetails
          .filter(entity => entity.hasProfTax && entity.profTaxNo)
          .map(entity => entity.profTaxNo);
          
        const duplicateProfTax = profTaxNumbers.filter(
          (taxNo, index) => profTaxNumbers.indexOf(taxNo) !== index
        );
        
        if (duplicateProfTax.length > 0) {
          errorMessage += `- Duplicate Professional Tax number(s) found: ${duplicateProfTax.join(', ')}\n`;
          newErrors.duplicateProfTax = true;
          if (!firstErrorField) firstErrorField = 'taxNumbers';
        }
        
        // Check for duplicate trade license numbers
        const tradeLicenseNumbers = entityDetails
          .filter(entity => entity.hasTradeLicense && entity.tradeLicenseNo)
          .map(entity => entity.tradeLicenseNo);
          
        const duplicateTradeLicense = tradeLicenseNumbers.filter(
          (licenseNo, index) => tradeLicenseNumbers.indexOf(licenseNo) !== index
        );
        
        if (duplicateTradeLicense.length > 0) {
          errorMessage += `- Duplicate Trade License number(s) found: ${duplicateTradeLicense.join(', ')}\n`;
          newErrors.duplicateTradeLicense = true;
          if (!firstErrorField) firstErrorField = 'taxNumbers';
        }
      }
    }
    
    // Check if employee count exceeds salaried employees for non-residential entities
    const entitiesWithExcessEmployees = entityDetails.filter(entity => 
      entity.firmType !== 'residential' && 
      Object.values(entity.employeeCounts || {}).reduce((sum, count) => sum + (parseInt(count) || 0), 0) > 
      (entity.salariedEmployees || 0)
    );

    if (entitiesWithExcessEmployees.length > 0) {
      errorMessage += '- Total employees cannot exceed salaried employees count\n';
      newErrors.salariedEmployees = true;
      if (!firstErrorField) firstErrorField = 'salariedEmployees';
    }
    
    // Enhanced Professional Tax Validation for non-residential entities
    if (propertyDetails.buildingUsage === 'commercial' || propertyDetails.buildingUsage === 'mixed' || 
        propertyDetails.buildingUsage === 'educational' || propertyDetails.buildingUsage === 'government' || 
        propertyDetails.buildingUsage === 'industrial') {
      
      const invalidEntities = entityDetails.filter(entity => {
        if (!entity.firmType) return false;
        // Skip residential entities
        if (entity.firmType === 'residential') return false;
        
        if (entity.hasProfTax || entity.hasTradeLicense) {
          // Check if at least one tax type is selected and has a license number
          if (entity.hasProfTax && !entity.profTaxNo) return true;
          if (entity.hasTradeLicense && !entity.tradeLicenseNo) return true;
          return false;
        } else {
          // If no tax types are selected, check firm details
          return !entity.firmName;
        }
      });

      if (invalidEntities.length > 0) {
        errorMessage += '- Please provide ';
        const missingTaxNumbers = invalidEntities.some(e => (e.hasProfTax && !e.profTaxNo) || (e.hasTradeLicense && !e.tradeLicenseNo));
        if (missingTaxNumbers) {
          errorMessage += 'tax numbers for selected registration types';
          newErrors.taxNumbers = true;
          if (!firstErrorField) firstErrorField = 'taxNumbers';
        } else {
          errorMessage += 'firm name for occupancies without tax numbers';
          newErrors.firmDetails = true;
          if (!firstErrorField) firstErrorField = 'firmDetails';
        }
        errorMessage += '\n';
      }
    }

    // Building Type validation
    if (!buildingType) {
      errorMessage += '- Please select a building type\n';
      newErrors.buildingType = true;
      if (!firstErrorField) firstErrorField = 'buildingType';
    }

    // Apartment Specific Validations
    if (buildingType === 'apartment') {
      if (!totalFloors) {
        errorMessage += '- Please select total number of floors\n';
        newErrors.totalFloors = true;
        if (!firstErrorField) firstErrorField = 'totalFloors';
      }
      
      if (!apartmentFloor && apartmentFloor !== '0') {
        errorMessage += '- Please select the floor number\n';
        newErrors.apartmentFloor = true;
        if (!firstErrorField) firstErrorField = 'apartmentFloor';
      }

      // Roof Structure validation for top floor
      if (apartmentFloor === totalFloors && !buildingStructure) {
        errorMessage += '- Roof structure is required for top floor\n';
        newErrors.buildingStructure = true;
        if (!firstErrorField) firstErrorField = 'buildingStructure';
      }
    }

    // Independent or Row House Validations
    if (buildingType === 'independent' || buildingType === 'row_house') {
      if (!buildingStructure) {
        errorMessage += '- Roof structure is required\n';
        newErrors.buildingStructure = true;
        if (!firstErrorField) firstErrorField = 'buildingStructure';
      }
    }

    // Photo validation for all building types
    if (!propertyPhotos || propertyPhotos.length === 0) {
      errorMessage += '- At least one property photo is required\n';
      newErrors.propertyPhotos = true;
      if (!firstErrorField) firstErrorField = 'propertyPhotos';
    }
  } else {
    // Non-Building Validations
    if (!currentUsage) {
      errorMessage += '- Please select current usage\n';
      newErrors.currentUsage = true;
      if (!firstErrorField) firstErrorField = 'currentUsage';
    }

    if (!propertyPhotos || propertyPhotos.length === 0) {
      errorMessage += '- At least one property photo is required\n';
      newErrors.propertyPhotos = true;
      if (!firstErrorField) firstErrorField = 'propertyPhotos';
    }
  }

  // Handle errors
  if (errorMessage) {
    alert('Please fix the following issues:\n' + errorMessage);
    setErrors(newErrors);
    
    // Focus on the first error field
    setTimeout(() => {
      const element = document.getElementById(firstErrorField) || 
                     document.querySelector(`[name="${firstErrorField}"]`) ||
                     document.querySelector(`[data-field="${firstErrorField}"]`);
      
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }, 100);
    
    return true; // Return true to indicate validation failed
  }

  // Clear errors if everything is valid
  setErrors({});
  return false; // Return false to indicate validation passed
};
  const handleBack = () => navigate(-1);
  const handleSubmit = async () => {
    if (validateForm()) {
      return;
    }
  
    try {
      setIsSubmitting(true);
      const userData = JSON.parse(localStorage.getItem('userData'));
      const token = userData?.authToken;
  
      if (!token) {
        throw new Error('Authentication token not found');
      }
      // Separate location data from first photo
      const primaryLocation = propertyPhotos[0]?.location || null;
      const photoInfo = propertyPhotos.map(photo => ({
        path: photo.path
      }));
  
      // Location payload
      const locationPayload = primaryLocation ? {
        latitude: primaryLocation.lat,
        longitude: primaryLocation.lng
      } : null;
  
      // Create commercial data array for both commercial and mixed usage
      const commercialData = entityDetails.map(entity => {
        // Calculate total employees and tax for this entity
        const totalEmployees = Object.values(entity.employeeCounts || {})
          .reduce((sum, count) => sum + (parseInt(count) || 0), 0);
  
        const totalTax = Object.entries(entity.employeeCounts || {})
          .reduce((sum, [key, count]) => {
            const rowNum = key.replace('row', '');
            const taxRate = getTaxRateForRow(rowNum);
            return sum + calculateTax(parseInt(count) || 0, taxRate);
          }, 0);
  
        // Create employee details object
        const empDetails = {};
        Object.entries(entity.employeeCounts || {}).forEach(([key, count]) => {
          if (parseInt(count) > 0) {
            const rowNum = key.replace('row', '');
            empDetails[`range_${rowNum}`] = {
              range: getRangeForRow(rowNum),
              count: parseInt(count),
              tax: calculateTax(parseInt(count), getTaxRateForRow(rowNum))
            };
          }
        });
  
        // Return either professional tax data or firm details
        if (entity.hasLicense) {
          return {
            has_prof_tax: true,
            prof_tax_no: entity.licenseNo?.toString() || '',
            firm_name: '',
            owner_name: '',
            mobile_number: '',
            total_employees: totalEmployees,
            total_tax: totalTax,
            emp_details: empDetails
          };
        } else {
          return {
            has_prof_tax: false,
            prof_tax_no: '',
            firm_name: entity.firmName || '',
            owner_name: entity.ownerName || '',
            mobile_number: entity.mobileNumber || '',
            total_employees: totalEmployees,
            total_tax: totalTax,
            emp_details: empDetails
          };
        }
      });
  
      // Calculate total professional tax
      const totalProfessionalTax = commercialData.reduce((total, entity) => 
        total + (entity.total_tax || 0), 0);
  
      let residentialBusinessData;
      
      if (propertyDetails.buildingUsage === 'residential') {
        if (hasBusiness === 'yes') {
          if (businessLocation === 'withinULB') {
            residentialBusinessData = {
              has_business: true,
              business_location: {
                is_within_ulb: true,
                ward_no: businessWardNo || '',
                business_name: businessName || '',
                business_type: businessType || '',
                address: businessAddress || '',
                gst_number: businessGstNumber || ''
              }
            };
          } else {
            residentialBusinessData = {
              has_business: true,
              business_location: {
                is_within_ulb: false,
                location_type: externalLocationType || '',
                address: businessAddress || '',
                gst_number: businessGstNumber || ''
              }
            };
          }
        } else {
          residentialBusinessData = {
            has_business: false,
            business_location: null
          };
        }
      }
  
// Update the handleSubmit function in VerificationPage to include trade license data
// Add this inside the payload construction section before API call

// Update the payload construction in the handleSubmit function:
// Complete final payload structure
// In the handleSubmit function, modify the payload structure like this:
// Update the payload construction in the handleSubmit function
const payload = {
  asst_det_id: propertyData.id, 
  owner_det: {
    name: (ownerVerified ? propertyData?.owner : newOwnerName)?.trim() || '',
    mobile: mobileNumber?.toString()?.trim() || '',
    is_owner_verified: ownerVerified,
    is_mobile_verified: mobileVerified
  },
  
  // Simplified str_det with business information moved here (if residential)
  str_det: propertyDetails.buildingUsage === 'residential' && hasBusiness === 'yes' ? {
    // No hardcoded properties like floors, prop_floor, structure, type
    // Add business details directly here
    has_business: true,
    business_location: businessLocation === 'withinULB' ? {
      is_within_ulb: true,
      ward_no: businessWardNo || '',
      business_name: businessName || '',
      business_type: businessType || '',
      address: businessAddress || '',
      gst_number: businessGstNumber || ''
    } : {
      is_within_ulb: false,
      location_type: externalLocationType || '',
      address: businessAddress || '',
      gst_number: businessGstNumber || ''
    }
  } : {
    // For non-residential or residential without business
    has_business: false
  },
  
  area: (propertyDetails?.floorArea || '0').toString(),
  usage: (propertyDetails?.buildingUsage || '').charAt(0).toUpperCase() +
         (propertyDetails?.buildingUsage || '').slice(1).toLowerCase(),
  eb_num: (propertyDetails?.ebNumber || '').toString(),
  
  prof_tax: {
    has_tax: propertyDetails?.buildingUsage?.toLowerCase() !== 'residential',
    occupancy_count: propertyDetails?.buildingUsage?.toLowerCase() === 'residential' ? 0 : entityDetails.length,
    total_tax: entityDetails.reduce((total, entity) => {
      // Skip residential entities in tax calculation
      if (entity.firmType === 'residential') return total;
      
      return total + Object.entries(entity.employeeCounts || {}).reduce((sum, [key, count]) => {
        const rowNum = key.replace('row', '');
        const taxRate = getTaxRateForRow(rowNum);
        return sum + (parseInt(count) || 0) * taxRate;
      }, 0);
    }, 0),
    entities: entityDetails.map(entity => {
      // For residential entities, only include basic info
      if (entity.firmType === 'residential') {
        return {
          firm_type: 'residential',
          commercial_area: entity.commercialArea || '0',
          owner_type: entity.ownerType || 'same',
          owner_name: entity.ownerType === 'other' ? (entity.ownerName || '') : (propertyData?.owner || ''),
          mobile_number: entity.ownerType === 'other' ? (entity.mobileNumber || '') : (propertyData?.mobile_number || ''),
          // Skip tax-related fields for residential
          has_prof_tax: false,
          has_trade_license: false,
          gst_number: '',
          salaried_employees: 0,
          total_employees: 0,
          total_tax: 0
        };
      }
      
      // For non-residential entities, include all fields
      return {
        firm_type: entity.firmType || '',
        has_prof_tax: entity.hasProfTax || false,
        prof_tax_no: entity.hasProfTax ? entity.profTaxNo || '' : '',
        has_trade_license: entity.hasTradeLicense || false,
        trade_license_no: entity.hasTradeLicense ? entity.tradeLicenseNo || '' : '',
        gst_number: entity.gstNumber || '',
        commercial_area: entity.commercialArea || '0',
        firm_name: (!entity.hasProfTax && !entity.hasTradeLicense) ? (entity.firmName || '') : '',
        owner_type: entity.ownerType || 'same',
        owner_name: entity.ownerType === 'other' ? (entity.ownerName || '') : (propertyData?.owner || ''),
        mobile_number: entity.ownerType === 'other' ? (entity.mobileNumber || '') : (propertyData?.mobile_number || ''),
        salaried_employees: entity.salariedEmployees || 0,
        total_employees: Object.values(entity.employeeCounts || {})
          .reduce((sum, count) => sum + (parseInt(count) || 0), 0),
        total_tax: Object.entries(entity.employeeCounts || {}).reduce((sum, [key, count]) => {
          const rowNum = key.replace('row', '');
          const taxRate = getTaxRateForRow(rowNum);
          return sum + (parseInt(count) || 0) * taxRate;
        }, 0),
        emp_details: Object.entries(entity.employeeCounts || {}).reduce((acc, [key, count]) => {
          if (parseInt(count) > 0) {
            const rowNum = key.replace('row', '');
            acc[`range_${rowNum}`] = {
              range: getRangeForRow(rowNum),
              count: parseInt(count),
              tax: (parseInt(count) || 0) * getTaxRateForRow(rowNum)
            };
          }
          return acc;
        }, {})
      };
    }),
    commercial_area: entityDetails.reduce((total, entity) => 
      total + parseFloat(entity.commercialArea || 0), 0).toString()
  },
  
  // REMOVED: residential_business field is gone
  
  // Property images
  images: [
    ...propertyPhotos.map(photo => ({
      path: photo.path,
      type: 'property'
    }))
  ],
  
  location: propertyPhotos[0]?.location ? {
    latitude: propertyPhotos[0].location.lat,
    longitude: propertyPhotos[0].location.lng
  } : null
};
console.log('Final Payload:', payload);
  
      // Make the API call
      const response = await api.post('/survey', payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
  
      // Handle success
      if (response.data && !response.data.error) {
        localStorage.setItem('formData', JSON.stringify({
          ward_id: propertyData.ward_id,
          street_id: propertyData.street_id
        }));
        
        sessionStorage.setItem('fromVerification', 'true');
        sessionStorage.setItem('lastPath', '/verify');
  
        setShowSuccessNotification(true);
        
        setTimeout(() => {
          setShowSuccessNotification(false);
          navigate(-1);
        }, 2000);
      } else {
        throw new Error(response.data?.message || 'Failed to submit survey');
      }
  
    } catch (error) {
      console.error('Submit Error:', error);
      alert(error.message || 'Failed to submit survey');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Helper function to calculate total tax
  const calculateTotalTax = (entities) => {
    return entities.reduce((total, entity) => {
      const entityTax = Object.entries(entity.employeeCounts || {}).reduce((sum, [key, count]) => {
        const taxRate = getTaxRateForRow(key.replace('row', ''));
        return sum + calculateTax(count, taxRate);
      }, 0);
      return total + entityTax;
    }, 0);
  };
  // Helper functions
function getRangeForRow(rowNum) {
  const index = parseInt(rowNum) - 1;
  if (index >= 0 && index < slabRates.length) {
    return `${slabRates[index].SlabRateUnitFrom}-${slabRates[index].SlabRateUnitTo}`;
  }
  return '';
}

function getTaxRateForRow(rowNum) {
  const index = parseInt(rowNum) - 1;
  if (index >= 0 && index < slabRates.length) {
    return slabRates[index].ProfTaxSlabRate;
  }
  return 0;
}
  // Make sure these helper functions are present
  const compressImage = async (dataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Target size
        const maxWidth = 800;
        const maxHeight = 800;
        
        let width = img.width;
        let height = img.height;
        
        // Calculate dimensions
        if (width > height && width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        } else if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Compress with relatively low quality
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      };
      img.src = dataUrl;
    });
  };
  
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  const handlePropertyDetailsChange = (field, value) => {
    setPropertyDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading || !propertyData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent shadow-lg"></div>
      </div>
    );
  }

  const handleVerificationSubmit = async () => {
    try {
        const response = await fetch('https://ras-tndma.in/api/prod/survey', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                // Add any required data here, for example:
                // key1: value1,
                // key2: value2,
            }),
        });

        if (response.ok) {
            const data = await response.json();
            console.log("Success:", data);
            // Handle successful response here (e.g., show a success message)
        } else {
            console.log("Error:", response.status);
            // Handle error response here (e.g., show an error message)
        }
    } catch (error) {
        console.error("Request failed:", error);
        // Handle network or other errors here
    }
};


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-md backdrop-blur-lg bg-white/90">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button
                onClick={handleBack}
                className="text-gray-600 hover:text-gray-900 transition-colors p-2 hover:bg-gray-100 rounded-lg group"
              >
                <ArrowLeft className="h-6 w-6 group-hover:-translate-x-1 transition-transform" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <LayoutDashboard className="h-7 w-7 text-sky-500" />
                Property Verification
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">

      <DetailSection title="Property Information">
  <button
    className="lg:hidden w-full flex items-center justify-between p-2 mb-3 text-gray-700 bg-gray-100 rounded-lg"
    onClick={() => setIsMobileExpanded(!isMobileExpanded)}
  >
    <span className="font-semibold">Property Details</span>
    {isMobileExpanded ? (
      <ChevronUp className="h-4 w-4 text-gray-500" />
    ) : (
      <ChevronDown className="h-4 w-4 text-gray-500" />
    )}
  </button>
  <div className={`${!isMobileExpanded ? 'hidden lg:block' : ''}`}>
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
        {/* First Row */}
        <div>
          <p className="text-sm font-bold text-gray-700">Organization name</p>
          <p className="text-xs font-medium text-gray-500 mt-1">{propertyData.area_name}</p>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-700">Zone Name</p>
          <p className="text-xs font-medium text-gray-500 mt-1">{propertyData.zone_name}</p>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-700">Ward Number</p>
          <p className="text-xs font-medium text-gray-500 mt-1">{propertyData.ward_name}</p>
        </div>

        {/* Second Row */}
        <div>
          <p className="text-sm font-bold text-gray-700">Street</p>
          <p className="text-xs font-medium text-gray-500 mt-1">{propertyData.street_name}</p>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-700">Assessment Number</p>
          <p className="text-xs font-medium text-gray-500 mt-1">{propertyData.asst_ref}</p>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-700">EB Number</p>
          <p className="text-xs font-medium text-gray-500 mt-1">{propertyData.eb_number || '----'}</p>
        </div>
        
        {/* Third Row */}
        <div>
          <p className="text-sm font-bold text-gray-700">GST Number</p>
          <p className="text-xs font-medium text-gray-500 mt-1">{propertyData.gst_number || '----'}</p>
        </div>
      </div>
    </div>
  </div>
</DetailSection>
   
{/* Owner Verification Section */}
<DetailSection title="Owner Verification">
  <div className="space-y-4">
    <div className="flex flex-col md:flex-row items-start md:items-center gap-4 bg-sky-50 p-4 rounded-xl">
      <div className="h-12 w-12 bg-sky-500 rounded-full flex items-center justify-center">
        <User className="h-6 w-6 text-white" />
      </div>
      <div className="flex flex-col md:flex-row md:items-center md:gap-8 w-full">
        <div>
          <p className="text-sm text-gray-500">Owner Name</p>
          <h3 className="text-xl font-semibold text-gray-800">{propertyData.owner}</h3>
        </div>
        <div>
          <p className="text-sm text-gray-500">Mobile Number</p>
          <h3 className="text-xl font-semibold text-gray-800">{propertyData.mobile_number || '--'}</h3>
        </div>
      </div>
    </div>

    <div className="space-y-4 md:mt-4">
      {/* Owner and Mobile Verification - with smaller gap */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        {/* Owner Verification Toggle */}
        <div className="flex items-center">
          <p className="text-sm text-gray-600">Is this owner name correct</p>
          <button
            onClick={() => {
              setOwnerVerified(!ownerVerified);
              if (!ownerVerified) setNewOwnerName('');
            }}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full mx-2
              ${ownerVerified ? 'bg-green-500' : 'bg-gray-200'}
              transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2
            `}
          >
            <span className="sr-only">Toggle owner verification</span>
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition duration-200 ease-in-out
                ${ownerVerified ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
          <span className="text-sm text-gray-600">
            {ownerVerified ? 'Yes' : 'No'}
          </span>
        </div>
        
        {/* Mobile Verification Radio Buttons */}
        <div className="flex items-center">
          <p className="text-sm text-gray-600">Is this mobile number correct</p>
          <div className="ml-2 flex items-center">
            <label className="inline-flex items-center mr-2">
              <input
                type="radio"
                name="mobileVerified"
                checked={mobileVerified}
                onChange={() => {
                  setMobileVerified(true);
                  setMobileNumber(propertyData?.mobile_number || '');
                  setShowMobileError(false);
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-1 text-sm text-gray-600">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="mobileVerified"
                checked={!mobileVerified}
                onChange={() => {
                  setMobileVerified(false);
                  setMobileNumber('');
                  setShowMobileError(false);
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-1 text-sm text-gray-600">No</span>
            </label>
          </div>
        </div>
      </div>

      {(!ownerVerified || !mobileVerified) && (
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
          {!ownerVerified && (
          <input
            id="ownerName"
            type="text"
            value={newOwnerName}
            onChange={(e) => {
              const value = e.target.value.replace(/[^A-Za-z\s]/g, '');
              setNewOwnerName(value);
            }}
            onKeyPress={(e) => {
              if (!/[A-Za-z\s]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm"
            pattern="[A-Za-z\s]+"
            placeholder="Enter owner name"
          />
          )}
          {!mobileVerified && (
            <div>
              <input
                id="mobileNumber"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                maxLength="10"
                value={mobileNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setMobileNumber(value);
                  setIsMobileValid(value === '' || /^[6-9]\d{9}$/.test(value));
                  setShowMobileError(value.length === 10 && !/^[6-9]\d{9}$/.test(value));
                }}
                className={`w-full rounded-lg border p-2 text-sm focus:ring-2 focus:ring-sky-500 ${showMobileError || (!isMobileValid && mobileNumber) ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-sky-500'}`}
                placeholder="Enter correct 10-digit mobile number"
                aria-describedby="mobileNumberError"
              />
              {(showMobileError || (!isMobileValid && mobileNumber)) && (
                <p id="mobileNumberError" className="mt-1 text-sm text-red-600">Enter a valid mobile number starting with 6, 7, 8, or 9.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  </div>
</DetailSection>

{isBuilding && (
  <div className="mb-6 mt-16">
<PropertyDetailsForm
  onChange={handlePropertyDetailsChange}
  data={propertyDetails}
  propertyPhotos={propertyPhotos}
  setPropertyPhotos={setPropertyPhotos}
  previousArea={propertyData.build_area}
  previousUsage={propertyData.usage_type}
  entityDetails={entityDetails}
  setEntityDetails={setEntityDetails}
  propertyData={propertyData}
  commercialArea={commercialArea}
  setCommercialArea={setCommercialArea}
  hasBusiness={hasBusiness}
  setHasBusiness={setHasBusiness}
  businessLocation={businessLocation} 
  setBusinessLocation={setBusinessLocation}
  businessWardNo={businessWardNo}
  setBusinessWardNo={setBusinessWardNo}
  businessAddress={businessAddress}
  setBusinessAddress={setBusinessAddress}
  businessName={businessName}
  setBusinessName={setBusinessName}
  businessType={businessType}
  setBusinessType={setBusinessType}
  businessGstNumber={businessGstNumber}
  setBusinessGstNumber={setBusinessGstNumber}
  externalLocationType={externalLocationType}
  setExternalLocationType={setExternalLocationType}
  slabRates={slabRates}
  isLoadingSlabRates={isLoadingSlabRates}
/>
  </div>
)}
{/* Submit Button */}
<div className="flex justify-center mt-12 mb-12">
<button
  onClick={handleSubmit}
  disabled={isSubmitting}
  className="px-5 py-2 rounded-xl flex items-center gap-3 bg-sky-600 text-white 
  hover:bg-sky-700 transition-all duration-300 transform hover:-translate-y-1 
  hover:shadow-lg text-lg font-semibold group disabled:opacity-50 disabled:cursor-not-allowed"
>
  {isSubmitting ? (
    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
  ) : (
    <Check className="h-6 w-6 group-hover:scale-110 transition-transform" />
  )}
  {isSubmitting ? 'Submitting...' : 'Submit Survey'}
</button>
</div>


        {/* Success Notification Modal */}
        {showSuccessNotification && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-96 transform transition-all duration-300 animate-scaleIn">
              <div className="flex items-center justify-between px-8 py-6 border-b">
                <h3 className="text-2xl font-bold text-gray-800">Success</h3>
                <button
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowSuccessNotification(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="px-8 py-10">
                <div className="flex items-center justify-center mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-green-100 rounded-full animate-ping"></div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-20 w-20 text-green-500 relative z-10"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-xl text-center text-gray-700 font-medium">
                  Your data has been saved successfully!
                </p>
              </div>
              <div className="px-8 py-6 bg-gray-50 rounded-b-2xl flex justify-end">
                <button
                  className="px-6 py-3 text-white bg-blue-500 rounded-xl hover:bg-blue-600 transition-colors duration-300 font-semibold focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  onClick={() => setShowSuccessNotification(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationPage;
